import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { DurationFormat } from '@freelancer/pipes';
import { IconColor, IconName, IconSize } from '@freelancer/ui/icon';
import { PictureObjectFit } from '@freelancer/ui/picture';
import { textVariants } from '@freelancer/ui/text';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { BackgroundColor, ExperienceType } from './experience.types';

@Component({
  standalone: false,
  selector: 'fl-experience',
  template: `
    <div class="Container">
      @if (!imgSrc) {
        <div
          class="GenericIconContainer mr-small"
          [attr.data-background-color]="backgroundColor"
        >
          <fl-icon
            [fillIconFont]="false"
            [name]="placeholderIconName"
            [sizeTablet]="IconSize.LARGE"
            [size]="IconSize.MID"
            [useIconFont]="true"
            [color]="
              backgroundColor !== BackgroundColor.DEFAULT
                ? IconColor.LIGHT
                : IconColor.DARK
            "
          ></fl-icon>
        </div>
      }
      @if (imgSrc) {
        <fl-picture
          class="ExperienceImg mr-small"
          [src]="imgSrc"
          [aspectRatioValue]="'square'"
          [objectFit]="PictureObjectFit.COVER"
          [alignCenter]="true"
          [borderRadiusBottom]="true"
          [borderRadiusTop]="true"
        ></fl-picture>
      }
      <div class="DetailsContainer">
        <div class="HeaderContainer mb-xxxsmall tablet:mb-xxsmall">
          <p
            [class]="titleClasses"
            [class.mr-xxsmall]="true"
          >
            {{ mainTitle }}
          </p>
          <div class="EditIconPositioningContainer">
            <div class="EditIconContainer">
              @if (canEdit) {
                <fl-icon
                  class="Icon"
                  i18n-label="Edit icon label"
                  label="Edit"
                  [name]="'ui-edit'"
                  [color]="IconColor.FOREGROUND"
                  [hoverAnimation]="'highlight'"
                  [size]="IconSize.MID"
                  [clickable]="true"
                  [useIconFont]="true"
                  (click)="triggerEdit()"
                ></fl-icon>
              }
            </div>
          </div>
        </div>
        <div class="mb-xxsmall mobile:show">
          <ng-container [ngTemplateOutlet]="dateTemplate"></ng-container>
        </div>
        <div class="mb-xxsmall">
          <ng-content></ng-content>
        </div>
      </div>
      <div class="mobile:hide">
        <ng-container [ngTemplateOutlet]="dateTemplate"></ng-container>
      </div>
    </div>
    <ng-template #dateTemplate>
      <div class="DateExperienceContainer">
        <div
          class="DateExperienceContainer-wrapper mb-xxxsmall mr-xxsmall tablet:mr-none"
        >
          @if (startDate) {
            <p [class]="titleClasses">
              @if (experienceType === ExperienceType.EXPERIENCE) {
                {{ startDate | date: 'MMM, yyyy' }}
                @if (endDate || endDate !== undefined) {
                  - {{ endDate | date: 'MMM, yyyy' }}
                } @else {
                  <ng-container i18n="Present"> - Present </ng-container>
                }
              } @else {
                {{ startDate | date: 'yyyy' : 'UTC' }}
                @if (endDate || endDate !== undefined) {
                  - {{ endDate | date: 'yyyy' : 'UTC' }}
                } @else {
                  <ng-container i18n="Present"> - Present </ng-container>
                }
              }
            </p>
          }
          @if (singleDate) {
            <p [class]="titleClasses">
              {{ singleDate | date: 'yyyy' }}
            </p>
          }
        </div>
        @if (duration) {
          <p
            [class]="durationClasses"
            [class.mr-xxsmall]="true"
            [class.mobile:show]="true"
          >
            {{ '•' }}
          </p>
        }

        @if (duration) {
          <p
            [class]="durationClasses"
            [class.mb-xxxsmall]="true"
          >
            {{ duration | duration: DurationFormat.LONG : startDate | flAsync }}
          </p>
        }
      </div>
    </ng-template>
  `,
  styleUrls: ['./experience.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExperienceComponent implements OnInit, OnChanges {
  BackgroundColor = BackgroundColor;
  ExperienceType = ExperienceType;
  IconColor = IconColor;
  IconSize = IconSize;
  DurationFormat = DurationFormat;
  PictureObjectFit = PictureObjectFit;

  placeholderIconName: IconName;
  titleClasses: string;
  durationClasses: string;

  @Input() backgroundColor = BackgroundColor.DEFAULT;
  @Input() experienceType = ExperienceType.EXPERIENCE;
  @Input() imgSrc?: string;
  @Input() mainTitle?: string;
  @Input() size = TextSize.BODY_16;
  @Input() sizeDesktop?: TextSize;
  @Input() sizeDesktopXXLarge?: TextSize;

  /*
    By using startDate the UI will always display 2 text fields
    startDate & endDate or startDate & "Present" in case endDate is empty,
    as is required in Experience and Education
  */
  @Input() startDate?: number;
  @Input() endDate?: number;
  @Input() duration?: number;
  @Input() durationUntilPresent?: number = 0;

  /*
    By using singleDate the UI will always just 1 text field with a short format,
    as is used in Publications and Qualifications
  */
  @Input() singleDate?: number;

  /*
    Shows an edit icon next to the experience's title if true.
  */
  @Input() canEdit = false;

  @Output() clickEdit = new EventEmitter<void>();

  ngOnInit(): void {
    if (this.endDate === undefined && this.startDate !== undefined) {
      this.duration = Date.now() - this.startDate;
    }
    switch (this.experienceType) {
      case ExperienceType.EDUCATION:
        this.placeholderIconName = 'ui-education';
        break;
      case ExperienceType.PUBLICATION:
        this.placeholderIconName = 'ui-publication';
        break;
      case ExperienceType.QUALIFICATION:
        this.placeholderIconName = 'ui-bought-medal';
        break;
      case ExperienceType.EXPERIENCE:
      default:
        this.placeholderIconName = 'ui-suitcase';
        break;
    }

    this.computeClasses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      'size' in changes ||
      'sizeDesktop' in changes ||
      'sizeDesktopXXLarge' in changes
    ) {
      this.computeClasses();
    }
  }

  triggerEdit(): void {
    this.clickEdit.emit();
  }

  private computeClasses(): void {
    this.titleClasses = textVariants({
      size: this.size,
      sizeDesktop: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
      weight: FontWeight.MEDIUM,
    });

    this.durationClasses = textVariants({
      size: this.size,
      sizeDesktop: this.sizeDesktop,
      sizeDesktopXXL: this.sizeDesktopXXLarge,
    });
  }
}
